import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { X, IdCard, LogIn, ArrowRight, Sparkles, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useUserAuth } from "../context/UserAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiError = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    const first = d[0];
    if (first && typeof first === "object" && first.msg) return first.msg;
  }
  return "Errore";
};

/**
 * Dialog mostrato quando un utente senza card tenta di comprare.
 * - Se NON loggato: 2 CTA (login / iscriviti) + form inline
 * - Se loggato senza card: form inline pre-compilato (nome + email da user)
 */
export const CardGateDialog = ({ open, onClose }) => {
  const { user, refresh } = useUserAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Sync form with user when user resolves (or changes)
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      first_name: f.first_name || (user.name?.split(" ")[0] || ""),
      last_name: f.last_name || (user.name?.split(" ").slice(1).join(" ") || ""),
      email: f.email || user.email || "",
    }));
  }, [user]);

  if (!open) return null;

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/card-requests`, form);
      toast.success("Richiesta ricevuta. Ti contattiamo presto.");
      setDone(true);
      // Refresh user to update has_card flag (since backend set it)
      if (user) await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-black/85 backdrop-blur-md" />
      <div data-testid="card-gate-dialog" className="relative bg-zinc-950 border-4 border-yellow-400 max-w-2xl w-full max-h-[95vh] overflow-y-auto">
        {/* Top bar */}
        <div className="flex items-center justify-between p-5 border-b-2 border-zinc-800 sticky top-0 bg-zinc-950 z-10">
          <div className="flex items-center gap-3">
            <div className="bg-yellow-400 text-black p-2 brutal-shadow-sm">
              <IdCard className="w-5 h-5" strokeWidth={3} />
            </div>
            <div>
              <div className="font-jp text-yellow-400 text-[10px] tracking-widest">忠誠 / DAITA'N'CLUB</div>
              <div className="font-heading text-2xl uppercase text-white leading-none">Solo per Membri</div>
            </div>
          </div>
          <button data-testid="card-gate-close" onClick={onClose}
            className="p-2 text-white hover:bg-yellow-400 hover:text-black transition border-2 border-zinc-800">
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="card-gate-success">
            <div className="inline-flex items-center justify-center bg-yellow-400 text-black w-20 h-20 mb-5 brutal-shadow-black">
              <Sparkles className="w-10 h-10" strokeWidth={3} />
            </div>
            <h3 className="font-heading text-3xl uppercase text-white mb-3">Richiesta ricevuta</h3>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">
              Ti contatteremo via SMS/email per consegnarti la card. Da quel momento potrai acquistare e accumulare punti.
            </p>
            <button onClick={onClose} data-testid="card-gate-close-success"
              className="bg-yellow-400 text-black font-heading uppercase px-8 py-3 hover:bg-white transition">
              Capito
            </button>
          </div>
        ) : (
          <div className="p-6 sm:p-8 space-y-6">
            <div className="bg-zinc-900 border-2 border-yellow-400/40 p-4">
              <p className="text-white leading-relaxed">
                Su <span className="text-yellow-400 font-bold">Daita'n'Jack online</span> acquistano solo i membri del Club. È gratis, ti consegniamo la card e accumuli punti su ogni ordine.
              </p>
            </div>

            {/* Already member CTA */}
            {!user && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link to="/login" onClick={onClose} data-testid="card-gate-login"
                  className="bg-zinc-900 border-2 border-zinc-800 hover:border-yellow-400 p-5 transition group block">
                  <div className="flex items-center gap-2 text-yellow-400 mb-2">
                    <LogIn className="w-4 h-4" strokeWidth={3} />
                    <span className="font-heading uppercase text-xs tracking-widest">Sono già iscritto</span>
                  </div>
                  <div className="font-heading uppercase text-white text-lg leading-tight">Accedi alle offerte</div>
                  <div className="text-zinc-500 text-sm mt-1">Vai al tuo account e completa l'acquisto.</div>
                  <div className="text-yellow-400 text-xs uppercase tracking-widest mt-3 group-hover:underline flex items-center gap-1">
                    Login <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>

                <div className="bg-yellow-400 text-black border-2 border-yellow-400 p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <IdCard className="w-4 h-4" strokeWidth={3} />
                    <span className="font-heading uppercase text-xs tracking-widest">Non sei iscritto?</span>
                  </div>
                  <div className="font-heading uppercase text-lg leading-tight">Registrati qui</div>
                  <div className="text-black/70 text-sm mt-1">Compila il form qui sotto — è gratis.</div>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={submit} data-testid="card-gate-form" className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">Nome</label>
                  <input data-testid="gate-first-name" required value={form.first_name} onChange={onChange("first_name")}
                    className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-2.5 text-white focus:border-yellow-400 focus:outline-none" />
                </div>
                <div>
                  <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">Cognome</label>
                  <input data-testid="gate-last-name" required value={form.last_name} onChange={onChange("last_name")}
                    className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-2.5 text-white focus:border-yellow-400 focus:outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">Cellulare</label>
                  <input data-testid="gate-phone" required type="tel" value={form.phone} onChange={onChange("phone")}
                    placeholder="+39 ___ _______"
                    className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-2.5 text-white focus:border-yellow-400 focus:outline-none font-mono" />
                </div>
                <div>
                  <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">Email</label>
                  <input data-testid="gate-email" required type="email" value={form.email} onChange={onChange("email")}
                    disabled={!!user}
                    className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-2.5 text-white focus:border-yellow-400 focus:outline-none font-mono disabled:opacity-60" />
                </div>
              </div>
              <button type="submit" data-testid="gate-submit" disabled={loading}
                className="w-full bg-yellow-400 text-black font-heading uppercase tracking-widest text-base py-3 hover:bg-white transition flex items-center justify-center gap-2 disabled:opacity-40">
                {loading ? "Invio..." : "Voglio la card"} <Send className="w-4 h-4" strokeWidth={3} />
              </button>
              <p className="text-center text-zinc-500 text-xs uppercase tracking-widest">
                Richiedi online · Ritira in negozio a Viareggio (se vuoi)
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
